import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

gsap.registerPlugin(ScrollTrigger);

const DEFAULT_POSITIONS = [
  {
    position_key: 'prodavac',
    title: 'Prodavac IT opreme',
    subtitle: 'Prodaja i savetovanje klijenata',
    icon: 'ShoppingCart',
    description: 'Prodaja poslovne IT opreme, softvera i pratećih usluga klijentima.',
    requirements: ['Završena srednja škola', 'Poznavanje IT proizvoda', 'Komunikativnost', 'Engleski jezik - osnovni nivo'],
    benefits: ['Fiksna plata + provizija', 'Obuka o proizvodima', 'Radno vreme 6,5h dnevno', 'Mogućnost napredovanja']
  },
  {
    position_key: 'magacioner',
    title: 'Magacioner',
    subtitle: 'Rukovanje robom i skladištenjem',
    icon: 'Package',
    description: 'Prijem, skladištenje i otprema IT opreme, vođenje dokumentacije i kontrola stanja robe.',
    requirements: ['Završena srednja škola', 'Organizovanost', 'Fizička spremnost', 'Računarska pismenost'],
    benefits: ['Stabilna plata', 'Radno vreme 8h', 'Timski rad', 'Mogućnost dodatne edukacije']
  },
  {
    position_key: 'vozac',
    title: 'Vozač',
    subtitle: 'Dostava i transport IT opreme',
    icon: 'Truck',
    description: 'Sigurna i pravovremena isporuka proizvoda klijentima.',
    requirements: ['Važeća vozačka dozvola B kategorije', 'Minimum 3 godine iskustva', 'Poznavanje Beograda i okoline', 'Komunikativnost'],
    benefits: ['Plata + dnevnica', 'Službeno vozilo', 'Fleksibilno radno vreme', 'Terenski dodatak']
  },
  {
    position_key: 'serviser',
    title: 'Serviser IT opreme',
    subtitle: 'Popravka i održavanje IT opreme',
    icon: 'Wrench',
    description: 'Dijagnostika, popravka i održavanje računara i druge IT opreme, tehnička podrška klijentima.',
    requirements: ['Tehničko obrazovanje', 'Iskustvo sa hardverom i softverom', 'Preciznost u radu', 'Rešavanje problema'],
    benefits: ['Dodatne nadoknade', 'Obuka za nove tehnologije', 'Mogućnost karijernog razvoja', 'Sovremeni alati']
  },
  {
    position_key: 'knjigovodja',
    title: 'Administrativni radnik / Knjigovođa',
    subtitle: 'Podrška poslovnim procesima',
    icon: 'FileText',
    description: 'Vođenje administrativnih poslova, dokumentacije i finansijskih evidencija.',
    requirements: ['Srednja ili visoka stručna sprema', 'Organizovanost', 'Poznavanje rada na računaru', 'Engleski jezik - srednji nivo'],
    benefits: ['Stabilna plata', 'Rad u kancelariji', 'Mogućnost profesionalnog razvoja', 'Timski rad']
  },
  {
    position_key: 'hr-menadzer',
    title: 'HR menadžer',
    subtitle: 'Upravljanje ljudskim resursima',
    icon: 'Users',
    description: 'Planiranje i sprovođenje aktivnosti u oblasti ljudskih resursa i zapošljavanja.',
    requirements: ['VSS', 'Najmanje 3 godine iskustva u HR-u', 'Komunikacione i organizacione veštine', 'Poznavanje rada na računaru'],
    benefits: ['Konkurentna plata', 'Mogućnost profesionalnog razvoja', 'Rad u prijatnom okruženju', 'Timski rad']
  }
];

const createRepeatedPositions = (positions) => {
  if (!positions || positions.length === 0) return [];
  const repeatedPositions = [];
  for (let i = 0; i < 6; i++) {
    repeatedPositions.push({
      ...positions[i % positions.length],
      uniqueKey: `${positions[i % positions.length].position_key}-${i}`
    });
  }
  return repeatedPositions;
};

// FIX 1: Izvučeno van komponente da ne bi bilo rekreirano pri svakom renderu
const HexagonShape = memo(({ children, isCenter = false, onClick, delay = 0, size = 160 }) => {
  const hexagonSize = isCenter ? (size === 120 ? 120 : 180) : size;
  const strokeWidth = isCenter ? 3 : 2;

  return (
    <motion.div
      transition={{ duration: 0.4, delay, type: 'spring', stiffness: 150, damping: 15 }}
      className={`relative cursor-pointer transform transition-all duration-300 hover:scale-105 ${
        isCenter ? 'z-10' : 'hover:z-20'
      }`}
      onClick={onClick}
      style={{ width: hexagonSize, height: hexagonSize }}
    >
      <svg
        width={hexagonSize}
        height={hexagonSize}
        viewBox={`0 0 ${hexagonSize} ${hexagonSize}`}
        className="absolute inset-0"
      >
        <polygon
          points={`${hexagonSize/2},${strokeWidth*2} ${hexagonSize-strokeWidth*4},${hexagonSize/4} ${hexagonSize-strokeWidth*4},${3*hexagonSize/4} ${hexagonSize/2},${hexagonSize-strokeWidth*2} ${strokeWidth*4},${3*hexagonSize/4} ${strokeWidth*4},${hexagonSize/4}`}
          fill={isCenter ? '#701d2a' : '#9E1C2D'}
          stroke={isCenter ? '#9E1C2D' : '#701d2a'}
          strokeWidth={strokeWidth}
          className="transition-all duration-300 hover:fill-[#701d2a] hover:stroke-[#9E1C2D]"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
        {children}
      </div>
    </motion.div>
  );
});
HexagonShape.displayName = 'HexagonShape';

// FIX 1: Izvučeno van komponente + memo da spreči nepotrebne re-rendere
const JobHexagon = memo(({ job, delay, index, size = 160, onSelect }) => {
  const iconSize = size <= 100 ? 20 : size <= 120 ? 24 : 28;
  const textSize = size <= 100 ? 'text-[8px]' : size <= 120 ? 'text-[9px]' : 'text-xs';
  const textButton = size <= 100 ? 'text-[7px]' : size <= 120 ? 'text-[8px]' : 'text-xs';
  const { t } = useTranslation('jobs');

  return (
    <div>
      <HexagonShape
        onClick={() => onSelect(job)}
        delay={0}
        size={size}
      >
        <div className="w-full h-full flex flex-col items-center justify-center px-1">
          <Icon
            name={job.icon}
            size={iconSize}
            className="text-white mb-1"
          />
          <h3 className={`font-heading font-semibold ${textSize} text-white mb-1 leading-tight text-center`}>
            {job.title}
          </h3>
          <p className={`font-body ${textButton} text-primary-foreground opacity-90 text-center px-1 leading-tight underline`}>
            {t('hero.buttonText', 'Details')}
          </p>
        </div>
      </HexagonShape>
    </div>
  );
});
JobHexagon.displayName = 'JobHexagon';

const HexagonJobsSection = () => {
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  // FIX 4: Spojeni screenWidth i isMobile u jedan state da bi se izbegli dupli re-renderi pri resize
  const [screenState, setScreenState] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
    return { screenWidth: w, isMobile: w < 1024 };
  });
  const [jobPositions, setJobPositions] = useState(() => createRepeatedPositions(DEFAULT_POSITIONS));
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);
  const orbitContainerRef = useRef(null);
  const hexagonRefs = useRef([]);
  const { t } = useTranslation('jobs');

  const { screenWidth, isMobile } = screenState;

  // Fetch enabled positions from backend
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const response = await fetch('/backend/api/job_applications.php?path=positions');
        const contentType = response.headers.get('content-type') || '';

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        let data;
        if (contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const textData = await response.text();
          if (textData.trim().startsWith('<?php') || textData.trim().startsWith('<')) {
            throw new Error('Server returned PHP/HTML instead of JSON data');
          }
          data = JSON.parse(textData);
        }

        if (data.success && Array.isArray(data.data) && data.data.length > 0) {
          const positionsFromApi = data.data.map((pos) => ({
            position_key: pos.position_key,
            title: pos.title || pos.position_key || 'Pozicija',
            subtitle: pos.subtitle || '',
            icon: pos.icon || 'Briefcase',
            description: pos.description || '',
            requirements: Array.isArray(pos.requirements) ? pos.requirements : [],
            benefits: Array.isArray(pos.benefits) ? pos.benefits : []
          }));

          setJobPositions(createRepeatedPositions(positionsFromApi));
        } else {
          setJobPositions(createRepeatedPositions(DEFAULT_POSITIONS));
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.log('Using default job positions (API not ready or invalid response).', error);
        }
        setJobPositions(createRepeatedPositions(DEFAULT_POSITIONS));
      } finally {
        setLoading(false);
      }
    };
    fetchPositions();
  }, []);

  // FIX 4: Jedan setState poziv + debounce da smanje broj re-rendera pri resize
  useEffect(() => {
    let resizeTimer;
    const checkMobile = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const width = window.innerWidth;
        setScreenState({ screenWidth: width, isMobile: width < 1024 });
      }, 100);
    };

    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(resizeTimer);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    const element = sectionRef.current;
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const mobileRadius = useMemo(() => {
    return Math.max(120, Math.min(160, screenWidth * 0.32));
  }, [screenWidth]);

  // FIX 5: Dependency array smanjen — GSAP se rebuilda samo kad se promeni jobPositions,
  // ne i pri svakom resize eventu (isMobile uklonjen iz deps)
  useEffect(() => {
    const sectionEl = sectionRef.current;
    if (!sectionEl || !orbitContainerRef.current || jobPositions.length === 0 || hexagonRefs.current.length === 0) return;

    const scrollTriggerConfig = {
      trigger: sectionEl,
      start: 'top 80%',
      end: 'bottom 20%',
      scrub: 0.7,
      fastScrollEnd: true,
    };

    const ctx = gsap.context(() => {
      gsap.to(orbitContainerRef.current, {
        rotation: 360,
        ease: 'none',
        force3D: true,
        scrollTrigger: scrollTriggerConfig,
        transformOrigin: 'center center',
      });

      hexagonRefs.current.forEach((hexRef) => {
        if (!hexRef) return;
        gsap.to(hexRef, {
          rotation: -360,
          ease: 'none',
          force3D: true,
          scrollTrigger: scrollTriggerConfig,
          transformOrigin: 'center center',
        });
      });
    }, orbitContainerRef);

    // FIX 3: willChange se postavlja samo tokom animacije, a uklanja se kad ScrollTrigger završi
    const orbitEl = orbitContainerRef.current;
    const hexEls = hexagonRefs.current.filter(Boolean);
    const allEls = [orbitEl, ...hexEls];
    allEls.forEach(el => { if (el) el.style.willChange = 'transform'; });

    return () => {
      ctx.revert();
      allEls.forEach(el => { if (el) el.style.willChange = 'auto'; });
    };
  }, [jobPositions]); // FIX 5: isMobile uklonjen

  const centerHexagon = useMemo(() => ({
    title: 'PC CENTAR',
    subtitle: t('hero.joinTeam', 'Pridruži se našem timu'),
    icon: 'Building',
    description: t('hero.companyDesc', 'Vodeći distributer IT opreme sa više od 33 godine iskustva u pružanju profesionalnih IT usluga.')
  }), [t]);

  // FIX 1: useCallback da se ne kreira nova funkcija pri svakom renderu
  const handleSelectPosition = useCallback((job) => {
    setSelectedPosition(job);
  }, []);

  const structuredData = useMemo(() => {
    return {
      "@context": "https://schema.org/",
      "@type": "ItemList",
      "numberOfItems": DEFAULT_POSITIONS.length,
      "itemListElement": DEFAULT_POSITIONS.map((job, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "JobPosting",
          "title": job.title,
          "description": job.description,
          "hiringOrganization": {
            "@type": "Organization",
            "name": "PC Centar",
            "sameAs": "https://www.pccentar.rs"
          },
          "jobLocation": {
            "@type": "Place",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Beograd",
              "addressCountry": "RS"
            }
          },
          "datePosted": "2024-03-01",
          "employmentType": "FULL_TIME"
        }
      }))
    };
  }, []);

  // FIX 2: hexagonRefs.current se resetuje u useEffect, ne direktno u render telu
  useEffect(() => {
    hexagonRefs.current = [];
  }, [jobPositions]);

  return (
    <section ref={sectionRef} id="hexagon-jobs-section" style={{ contain: 'paint' }} className="py-20 bg-transparent relative overflow-hidden">
      {/* SEO: Nevidljiva skripta koja ne troši resurse pretraživača */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      <div className="absolute inset-0 hero-dark-overlay opacity-70 [data-theme='dark']:opacity-100 pointer-events-none"></div>

      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 opacity-10">
          <svg viewBox="0 0 48 48" className="w-full h-full">
            <polygon
              points="24,2 38,12 38,28 24,38 10,28 10,12"
              fill="var(--color-accent)"
            />
          </svg>
        </div>
        <div className="absolute bottom-20 right-10 w-24 h-24 opacity-10">
          <svg viewBox="0 0 48 48" className="w-full h-full">
            <polygon
              points="24,2 38,12 38,28 24,38 10,28 10,12"
              fill="var(--color-surface)"
            />
          </svg>
        </div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 opacity-5">
          <svg viewBox="0 0 48 48" className="w-full h-full">
            <polygon
              points="24,2 38,12 38,28 24,38 10,28 10,12"
              fill="var(--color-primary)"
            />
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading font-bold text-4xl text-foreground mb-4">
            {t('hero.title', 'Karijerne mogućnosti')}
          </h2>
          <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto">
            {t('hero.description', 'Pridruži se našem timu stručnjaka i razvij svoju karijeru u vodećoj distributivnoj kompaniji u Srbiji')}
          </p>
        </motion.div>

        {isMobile ? (
          <div className="relative flex justify-center items-center min-h-[320px] md:min-h-[380px]">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <HexagonShape isCenter={true} delay={0.1} size={120}>
                <Icon
                  name={centerHexagon.icon}
                  size={24}
                  className="text-primary-foreground mb-1"
                />
                <h3 className="font-heading font-bold text-[10px] text-primary-foreground mb-1 leading-tight">
                  {centerHexagon.title}
                </h3>
              </HexagonShape>
            </div>

            <div
              ref={orbitContainerRef}
              className="absolute top-1/2 left-1/2"
              style={{ transform: 'translate(-50%, -50%)' }}
            >
              {jobPositions.map((job, index) => {
                const angle = (index * 60) * (Math.PI / 180);
                const radius = mobileRadius;
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;

                return (
                  <div
                    key={job.uniqueKey}
                    className="absolute"
                    style={{
                      transform: `translate(${x}px, ${y}px)`,
                      left: '50%',
                      top: '50%',
                      marginLeft: "-50px",
                      marginTop: "-50px"
                    }}
                  >
                    <div ref={(el) => (hexagonRefs.current[index] = el)}>
                      <JobHexagon job={job} delay={0.1 + index * 0.05} index={index} size={100} onSelect={handleSelectPosition} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="relative flex justify-center items-center min-h-[400px] md:min-h-[500px] lg:min-h-[600px] w-full">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <HexagonShape isCenter={true} delay={0.1}>
                <Icon
                  name={centerHexagon.icon}
                  size={36}
                  className="text-primary-foreground mb-3"
                />
                <h3 className="font-heading font-bold text-sm text-primary-foreground mb-2">
                  {centerHexagon.title}
                </h3>
              </HexagonShape>
            </div>

            <div
              ref={!isMobile ? orbitContainerRef : undefined}
              className="absolute top-1/2 left-1/2"
              style={{ transform: 'translate(-50%, -50%)' }}
            >
              {jobPositions.map((job, index) => {
                const angle = (index * 60) * (Math.PI / 180);
                const radius = 220;
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;

                return (
                  <div
                    key={job.uniqueKey}
                    className="absolute"
                    style={{
                      transform: `translate(${x}px, ${y}px)`,
                      left: '50%',
                      top: '50%',
                      marginLeft: '-70px',
                      marginTop: '-70px'
                    }}
                  >
                    <div ref={(el) => (hexagonRefs.current[index] = el)}>
                      <JobHexagon job={job} delay={0.1 + index * 0.05} index={index} size={140} onSelect={handleSelectPosition} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedPosition && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 z-50 flex items-center justify-center p-6"
            onClick={() => setSelectedPosition(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card rounded-2xl p-8 max-w-2xl w-full border border-border shadow-2xl max-h-[70vh] md:max-h-[80vh] overflow-y-auto mt-20"
              onClick={(e) => e.stopPropagation()}
              onWheel={(e) => e.stopPropagation()}
            >
              <div className="flex items-center mb-6">
                <Icon name={selectedPosition.icon || 'Briefcase'} size={40} className="text-primary mr-4" />
                <div>
                  <h3 className="font-heading font-bold text-2xl text-foreground">
                    {selectedPosition.title}
                  </h3>
                  <p className="text-text-secondary">{selectedPosition.subtitle}</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="font-body text-foreground leading-relaxed" style={{ whiteSpace: 'pre-line' }}>
                  {selectedPosition.description}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="font-heading font-semibold text-foreground mb-3">{t('requirements', 'Zahtevi:')}</h4>
                  <ul className="space-y-2">
                    {(selectedPosition.requirements || []).map((req, index) => (
                      <li key={index} className="flex items-start">
                        <Icon name="Check" size={16} className="text-success mr-2 mt-1" />
                        <span className="font-body text-text-secondary">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-heading font-semibold text-foreground mb-3">{t('benefits', 'Benefiti:')}</h4>
                  <ul className="space-y-2">
                    {(selectedPosition.benefits || []).map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <Icon name="Star" size={16} className="text-warning mr-2 mt-1" />
                        <span className="font-body text-text-secondary">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setSelectedPosition(null)}
                  className="px-6 py-2 font-body text-text-secondary hover:text-foreground transition-colors"
                >
                  {t('close', 'Zatvori')}
                </button>
                <button
                  onClick={() => {
                    setSelectedPosition(null);
                    const formElement = document.getElementById('job-application-form');
                    if (formElement) {
                      formElement.scrollIntoView({ behavior: 'smooth' });
                      window.dispatchEvent(new CustomEvent('jobSelected', { detail: selectedPosition.position_key }));
                    }
                  }}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-body font-medium hover:bg-accent transition-colors"
                >
                  {t('apply', 'Prijavi se')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default HexagonJobsSection;